'use client'

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import { FormWidgetProps } from '@interfaces/giCar'
import { TYPING_SPEED_MS, ERASE_SPEED_MS, HOLD_MS, BETWEEN_MS, CURSOR_BLINK_MS, PLACEHOLDER_PREFIX } from '@constants/giCar'
import { randomPlate } from '@helpers/giCarHelpers'
import { IMAGES } from './config'
import styles from './FormWidget.module.scss'

const FormWidget: React.FC<FormWidgetProps> = ({
  registrationNumber,
  error,
  isLoading,
  onChangeHandler,
  onBlurHandler,
  onClickHandler,
  onKeyPressHandler,
  formData,
  animatePlaceholder = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [displayPlaceholder, setDisplayPlaceholder] = useState('')
  const [isInstruction, setIsInstruction] = useState(true)
  const [showCursor, setShowCursor] = useState(true)

  // Two-phrase infinite loop (car page only):
  // phrase 0 → instruction text appears instantly, holds, then clears instantly
  // phrase 1 → random plate types & erases with typewriter animation
  useEffect(() => {
    if (!animatePlaceholder) return

    const INSTRUCTION = 'Enter your car number'
    let currentText = `${PLACEHOLDER_PREFIX}${randomPlate()}`
    let charIndex = 0
    type Phase = 'typing' | 'holding' | 'erasing'
    let phase: Phase = 'typing'
    let phraseIndex = 0
    let timer: ReturnType<typeof setTimeout>

    setDisplayPlaceholder(INSTRUCTION)
    setIsInstruction(true)

    const tick = () => {
      if (phraseIndex === 0) {
        // Instruction — instant show, then clear and move to plate
        setDisplayPlaceholder('')
        setIsInstruction(false)
        phraseIndex = 1
        charIndex = 0
        phase = 'typing'
        timer = setTimeout(tick, BETWEEN_MS)
      } else if (phase === 'typing') {
        charIndex++
        setDisplayPlaceholder(currentText.slice(0, charIndex))
        if (charIndex >= currentText.length) {
          phase = 'holding'
          timer = setTimeout(tick, HOLD_MS)
        } else {
          timer = setTimeout(tick, TYPING_SPEED_MS)
        }
      } else if (phase === 'holding') {
        phase = 'erasing'
        timer = setTimeout(tick, ERASE_SPEED_MS)
      } else {
        charIndex--
        setDisplayPlaceholder(currentText.slice(0, charIndex))
        if (charIndex <= 0) {
          phraseIndex = 0
          currentText = `${PLACEHOLDER_PREFIX}${randomPlate()}`
          setDisplayPlaceholder(INSTRUCTION)
          setIsInstruction(true)
          timer = setTimeout(tick, HOLD_MS)
        } else {
          timer = setTimeout(tick, ERASE_SPEED_MS)
        }
      }
    }

    timer = setTimeout(tick, HOLD_MS)
    return () => clearTimeout(timer)
  }, [animatePlaceholder])

  // Always-blinking cursor (car page only)
  useEffect(() => {
    if (!animatePlaceholder) return

    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev)
    }, CURSOR_BLINK_MS)

    return () => clearInterval(cursorTimer)
  }, [animatePlaceholder])

  return (
    <div className={styles.formWidgetContainer}>
      <div className={styles.formWrapper}>
        <div className={styles.inputSection}>
          <div className={`${styles.inputWrapper} ${isFocused ? styles.focused : ''} ${error ? styles.error : ''}`}>
            <div className={styles.countryBadge}>
              <div className={styles.logoIcon}>
                <Image
                  src={IMAGES.FLAG.url}
                  alt={IMAGES.FLAG.alt}
                  width={IMAGES.FLAG.width}
                  height={IMAGES.FLAG.height}
                  margin-bottom={2}
                  priority
                />
              </div>
              <div className={styles.indText}>
                <Image
                  src={IMAGES.IND.url}
                  alt={IMAGES.IND.alt}
                  width={IMAGES.IND.width}
                  height={IMAGES.IND.height}
                  priority
                />
              </div>
            </div>
            <input
              ref={inputRef}
              type="text"
              className={styles.input}
              value={registrationNumber}
              onChange={e => {
                const upperValue = e.target.value.toUpperCase()
                onChangeHandler(upperValue)
              }}
              onBlur={() => {
                setIsFocused(false)
                onBlurHandler()
              }}
              onFocus={() => setIsFocused(true)}
              onKeyPress={onKeyPressHandler}
              placeholder={
                animatePlaceholder
                  ? isFocused
                    ? ''
                    : `${displayPlaceholder}${!isInstruction && showCursor ? '┃' : ''}`
                  : formData?.PLACEHOLDER
              }
              disabled={isLoading}
              maxLength={formData?.MAX_LENGTH}
              autoCapitalize="characters"
            />
          </div>
          {error && (
            <div className={styles.errorMessage}>
              <svg className={styles.errorIcon} viewBox="0 0 24 24" width="16" height="16">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              {formData?.ERROR_MESSAGE}
            </div>
          )}
          <div className={styles.buttonSection}>
            <button
              type="button"
              className={styles.submitButton}
              onClick={onClickHandler}
              disabled={isLoading || !registrationNumber.trim()}
              aria-label={formData?.BUTTON_TEXT}>
              {isLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  <span>{formData?.LOADING_TEXT}</span>
                </>
              ) : (
                formData?.BUTTON_TEXT
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FormWidget
